(() => {
  if (customElements.get('rettrose-review-grid')) return;
  const archives = new Map();
  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  const reviewDateFormatter = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' });
  const dateLabel = date => {
    const parsed = new Date(date);
    return Number.isNaN(parsed.valueOf()) ? '' : reviewDateFormatter.format(parsed);
  };
  class RettroseReviewGrid extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        if (this.reviews) this.observeCards();
        else this.startWhenVisible();
        return;
      }
      this.initialized = true;
      this.grid = this.querySelector('.rr-reviews__grid');
      this.status = this.querySelector('.rr-reviews__status');
      this.more = this.querySelector('.rr-reviews__more');
      this.filter = this.querySelector('[data-rating-filter]');
      this.sort = this.querySelector('[data-sort]');
      this.photosOnly = this.querySelector('[data-photos-only]');
      this.dialog = this.querySelector('dialog');
      this.pageSize = Number(this.dataset.pageSize) || 12;
      this.limit = this.pageSize;
      this.observer = new ResizeObserver(() => this.scheduleLayout());
      this.observer.observe(this.grid);
      [this.filter, this.sort, this.photosOnly].forEach(control => control.addEventListener('change', () => {
        this.limit = this.pageSize;
        this.render(true);
      }));
      this.more.addEventListener('click', () => {
        const previous = this.limit;
        // Preserve a card in the current viewport, rather than anchoring to the
        // load-more button as it moves below the newly appended reviews.
        const visible = [...this.grid.children].filter(card => {
          const rect = card.getBoundingClientRect();
          return rect.bottom > 0 && rect.top < window.innerHeight;
        });
        const anchor = visible[0] || this.grid.lastElementChild;
        this.scrollAnchor = anchor ? { node: anchor, top: anchor.getBoundingClientRect().top } : null;
        this.limit += this.pageSize;
        this.render(true, true);
        this.pendingFocus = this.grid.children[previous];
      });
      this.querySelector('.rr-reviews__close').addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('click', event => { if (event.target === this.dialog) this.dialog.close(); });
      document.fonts?.ready.then(() => this.scheduleLayout());
      this.startWhenVisible();
    }
    startWhenVisible() {
      if (this.loadingStarted || this.hidden) return;
      this.visibilityObserver?.disconnect();
      if (!('IntersectionObserver' in window)) { this.loadingStarted = true; this.load(); return; }
      this.visibilityObserver = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        this.visibilityObserver.disconnect();
        if (this.loadingStarted || this.hidden) return;
        this.loadingStarted = true;
        this.load();
      }, { rootMargin: '350px 0px' });
      this.visibilityObserver.observe(this);
    }
    disconnectedCallback() { this.visibilityObserver?.disconnect(); this.observer?.disconnect(); cancelAnimationFrame(this.frame); }
    async load() {
      try {
        const url = this.dataset.source;
        if (!archives.has(url)) archives.set(url, fetch(url).then(response => {
          if (!response.ok) throw new Error('Review archive unavailable');
          return response.json();
        }).catch(error => { archives.delete(url); throw error; }));
        const archive = await archives.get(url);
        this.imageSizes = archive.imageSizes || {};
        const handle = this.dataset.productHandle;
        this.reviews = archive.reviews.filter(review => !handle || review.productHandle === handle);
        if (!this.reviews.length) {
          this.status.textContent = 'No reviews yet';
          return;
        }
        this.summary();
        this.querySelector('.rr-reviews__content').hidden = false;
        this.render();
      } catch (error) {
        this.status.textContent = 'Customer reviews could not load. Please refresh the page to try again.';
      }
    }
    summary() {
      const count = this.reviews.length;
      const average = this.reviews.reduce((sum, review) => sum + review.rating, 0) / count;
      this.querySelector('[data-average]').textContent = `${average.toFixed(1)}/5`;
      this.querySelector('[data-count]').textContent = `${count.toLocaleString()} reviews`;
      this.querySelector('[data-total]').textContent = `${count.toLocaleString()} Reviews`;
      this.querySelector('[data-star-fill]').style.width = `${average * 20}%`;
      this.querySelector('.rr-reviews__aggregate').setAttribute('aria-label', `${average.toFixed(2)} out of 5 stars`);
      const breakdown = this.querySelector('.rr-reviews__breakdown');
      for (let rating = 5; rating >= 1; rating--) {
        const total = this.reviews.filter(review => review.rating === rating).length;
        const button = element('button', 'rr-reviews__rating-row');
        button.type = 'button';
        button.setAttribute('aria-label', `Show ${total} reviews rated ${rating} stars`);
        const progress = element('progress'); progress.max = count; progress.value = total;
        button.append(element('span', '', `${rating} ★`), progress, element('span', '', total));
        button.addEventListener('click', () => {
          this.filter.value = String(rating); this.limit = this.pageSize; this.render(true);
          this.querySelector('.rr-reviews__ratings').open = false;
        });
        breakdown.append(button);
      }
    }
    image(filename, className, alt, lazy = true) {
      const image = element('img', className);
      image.src = new URL(filename, new URL(this.dataset.assetBase, document.baseURI)).href;
      image.alt = alt;
      const size = this.imageSizes?.[filename];
      if (size) { image.width = size[0]; image.height = size[1]; }
      image.loading = lazy ? 'lazy' : 'eager';
      image.decoding = 'async';
      image.addEventListener('load', () => this.scheduleLayout());
      image.addEventListener('error', () => { image.hidden = true; this.scheduleLayout(); });
      return image;
    }
    card(review) {
      const card = element('article', 'rr-reviews__card');
      card.dataset.rating = review.rating;
      card.dataset.reviewId = review.id;
      if (review.images.length) {
        const photoButton = element('button', 'rr-reviews__photo-button');
        photoButton.type = 'button';
        photoButton.setAttribute('aria-label', `View ${review.name}'s review photos`);
        photoButton.append(this.image(review.images[0], 'rr-reviews__media', `Customer photo from ${review.name}`));
        if (review.images.length > 1) photoButton.append(element('span', 'rr-reviews__photo-count', `+${review.images.length - 1}`));
        photoButton.addEventListener('click', () => this.openPhoto(review));
        card.append(photoButton);
      }
      const body = element('div', 'rr-reviews__body');
      body.append(element('div', 'rr-reviews__author', review.name));
      const date = element('time', 'rr-reviews__date', dateLabel(review.date)); date.dateTime = review.date; body.append(date);
      const stars = element('div', 'rr-reviews__stars', '★'.repeat(review.rating) + '☆'.repeat(5 - review.rating));
      stars.setAttribute('role', 'img'); stars.setAttribute('aria-label', `${review.rating} out of 5 stars`); body.append(stars);
      if (review.title) body.append(element('h3', '', review.title));
      body.append(element('p', 'rr-reviews__text', review.body));
      card.append(body);
      if (review.reply) {
        const reply = element('details', 'rr-reviews__reply');
        reply.append(element('summary', '', 'Rettrose replied'), element('p', '', review.reply));
        card.append(reply);
      }
      if (review.productHandle) {
        const footer = element('div', 'rr-reviews__product');
        const link = element('a');
        link.href = `${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(review.productHandle)}`;
        if (review.productImage) {
          const image = element('img');
          const url = new URL(review.productImage); url.searchParams.set('width', '120');
          image.src = url.href; image.alt = ''; image.loading = 'lazy'; image.width = 56; image.height = 56;
          link.append(image);
        }
        link.append(element('span', '', review.productTitle || review.productHandle.replaceAll('-', ' ')));
        footer.append(link); card.append(footer);
      }
      return card;
    }
    render(announce = false, append = false) {
      if (!this.reviews) return;
      const matches = this.reviews.filter(review => (this.filter.value === 'all' || review.rating === Number(this.filter.value)) && (!this.photosOnly.checked || review.images.length));
      matches.sort((a, b) => {
        if (this.sort.value === 'photos' && Boolean(a.images.length) !== Boolean(b.images.length)) return b.images.length ? 1 : -1;
        if (this.sort.value === 'highest' && a.rating !== b.rating) return b.rating - a.rating;
        if (this.sort.value === 'lowest' && a.rating !== b.rating) return a.rating - b.rating;
        return new Date(b.date) - new Date(a.date);
      });
      this.observer.disconnect();
      const shown = matches.slice(0, this.limit);
      const existing = [...this.grid.children];
      const canAppend = append && existing.every((card, index) => card.dataset.reviewId === shown[index]?.id);
      if (canAppend) {
        this.grid.append(...shown.slice(existing.length).map(review => this.card(review)));
      } else {
        this.grid.replaceChildren(...shown.map(review => this.card(review)));
      }
      this.more.hidden = matches.length <= this.limit;
      this.status.textContent = !matches.length ? 'No reviews match these filters.' : announce ? `Showing ${Math.min(this.limit, matches.length)} of ${matches.length} reviews` : '';
      this.observeCards();
    }
    observeCards() {
      if (!this.observer || !this.isConnected) return;
      this.observer.observe(this.grid);
      [...this.grid.children].forEach(card => this.observer.observe(card));
      this.scheduleLayout();
    }
    scheduleLayout() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => {
        if (!this.isConnected) return;
        this.grid.dataset.masonry = '';
        const gap = parseFloat(getComputedStyle(this.grid).rowGap) || 0;
        const cards = [...this.grid.children];
        const spans = cards.map(card => Math.ceil((card.getBoundingClientRect().height + gap) / (1 + gap)));
        cards.forEach((card, index) => { card.style.gridRowEnd = `span ${spans[index]}`; });
        if (this.pendingFocus) {
          this.pendingFocus.tabIndex = -1;
          this.pendingFocus.focus({ preventScroll: true });
          this.pendingFocus = null;
        }
        if (this.scrollAnchor?.node.isConnected) {
          const delta = this.scrollAnchor.node.getBoundingClientRect().top - this.scrollAnchor.top;
          if (Math.abs(delta) > 0.5) window.scrollBy({ top: delta, behavior: 'instant' });
        }
        this.scrollAnchor = null;
      });
    }
    openPhoto(review) {
      const content = this.querySelector('.rr-reviews__dialog-content');
      content.replaceChildren();
      const photos = element('div', 'rr-reviews__dialog-photos');
      review.images.forEach(file => photos.append(this.image(file, '', `Customer photo from ${review.name}`, false)));
      const detail = this.card({ ...review, images: [] });
      content.append(photos, detail);
      this.dialog.showModal();
    }
  }
  customElements.define('rettrose-review-grid', RettroseReviewGrid);
})();
